import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { poppins } from '../helpers';
import { ActionState, GroupMode, TabKey, WorkListTabConfig } from '../types';

const Badge: React.FC<{ count: number; color: string }> = ({ count, color }) => (
    <Box sx={{ minWidth: 15, height: 15, borderRadius: '8px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
        <Box component="span" sx={{ color: '#fff', fontSize: '9px', fontWeight: 700, fontFamily: poppins, lineHeight: 1 }}>{count}</Box>
    </Box>
);

const getActionBtnStyle = (isActive: boolean, isSelected: boolean) => {
    let color: string;
    let background: string;
    let hoverBg: Record<string, string>;

    if (isSelected) {
        color = '#fff';
        background = '#2196F3';
        hoverBg = { background: '#1976D2' };
    } else if (isActive) {
        color = '#2196F3';
        background = 'rgba(33, 150, 243, 0.15)';
        hoverBg = { background: 'rgba(33,150,243,0.28)' };
    } else {
        color = '#BDBDBD';
        background = 'transparent';
        hoverBg = {};
    }

    return {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '5px 6px',
        cursor: isActive ? 'pointer' : 'default',
        fontFamily: poppins,
        fontSize: '12px',
        fontWeight: isSelected ? 600 : 400,
        color,
        background,
        transition: 'all 0.15s',
        '&:hover': hoverBg,
    };
};

const BtnGroup: React.FC<{
    label: string;
    isActive: boolean;
    actionState: ActionState;
    onSelect: () => void;
    onOpen: () => void;
    onClose: () => void;
}> = ({ label, isActive, actionState, onSelect, onOpen, onClose }) => {
    const openSelected = isActive && actionState === 'open';
    const closeSelected = isActive && actionState === 'close';

    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', borderRadius: '4px', border: `1px solid ${isActive ? '#2196F3' : '#BDBDBD'}`, overflow: 'hidden', flex: 1, transition: 'border-color 0.15s' }}>
            <Box onClick={isActive ? onOpen : undefined} sx={getActionBtnStyle(isActive, openSelected)}>Aç</Box>
            <Box sx={{ width: '1px', background: isActive ? '#2196F3' : '#BDBDBD', flexShrink: 0, transition: 'background 0.15s' }} />
            <Box onClick={onSelect} sx={{ flex: 1.3, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 6px', cursor: 'pointer', fontFamily: poppins, fontSize: '12px', fontWeight: isActive ? 600 : 400, color: isActive ? '#fff' : '#2196F3', background: isActive ? '#2196F3' : 'transparent', transition: 'all 0.15s', '&:hover': isActive ? { background: '#1976D2' } : { background: 'rgba(33,150,243,0.1)' } }}>
                {label}
            </Box>
            <Box sx={{ width: '1px', background: isActive ? '#2196F3' : '#BDBDBD', flexShrink: 0, transition: 'background 0.15s' }} />
            <Box onClick={isActive ? onClose : undefined} sx={getActionBtnStyle(isActive, closeSelected)}>Kapat</Box>
        </Box>
    );
};

export const WorkListHeaderSection: React.FC<{
    activeTab: TabKey;
    tabs: WorkListTabConfig[];
    onTabChange: (tab: TabKey) => void;
    onFilterClick: () => void;
    onClose: () => void;
}> = ({ activeTab, tabs, onTabChange, onFilterClick, onClose }) => (
    <Box sx={{ background: '#25388D', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 10px 16px' }}>
            <Box sx={{ fontFamily: poppins, fontSize: '17px', fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>Hemşire İş Listesi</Box>
            <Box onClick={onClose} sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', borderRadius: '6px', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </Box>
        </Box>
        <Box sx={{ padding: '0 10px 0' }}>
            <Box sx={{ background: '#30449E', borderRadius: '16px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ background: '#F4F4F4', borderRadius: '8px', border: '1px solid #DBE2EB', display: 'flex', alignItems: 'center', padding: '6px', gap: '4px' }}>
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        const isAll = tab.key === 'all';

                        return (
                            <Tooltip key={tab.key} title={tab.label} placement="top" arrow>
                                <Box onClick={() => onTabChange(tab.key)} sx={{ flex: isActive || isAll ? 1.8 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: isActive ? '100px' : '10px', background: isActive ? '#fff' : 'transparent', boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.15)' : 'none', padding: '8px 4px', minHeight: '38px', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { background: isActive ? '#fff' : 'rgba(255,255,255,0.5)' }, position: 'relative', overflow: 'hidden' }}>
                                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)', transform: isAll ? 'translateX(-16px)' : 'translateX(0)' }}>
                                        {tab.icon(isActive)}
                                        {!isAll && <Box sx={{ position: 'absolute', top: -8, right: -10 }}><Badge count={tab.badges[0].count} color={tab.badges[0].color} /></Box>}
                                    </Box>
                                    {isAll && <Box sx={{ position: 'absolute', top: 4, right: 6, display: 'flex', flexDirection: 'row', gap: '1px' }}>{tab.badges.map((badge, index) => <Badge key={index} count={badge.count} color={badge.color} />)}</Box>}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
                <Box onClick={onFilterClick} sx={{ background: '#F4F4F4', border: '1px solid #DBE2EB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '7px', cursor: 'pointer', '&:hover': { background: '#eee' } }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                        <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
                    </svg>
                    <Box sx={{ fontFamily: poppins, fontSize: '14px', fontWeight: 400, color: '#000', lineHeight: '20px' }}>Filtre</Box>
                </Box>
            </Box>
        </Box>
        <Box sx={{ height: 14 }} />
    </Box>
);

export const WorkListSearchSection: React.FC<{
    searchValue: string;
    onSearchChange: (value: string) => void;
    groupMode: GroupMode;
    actionState: ActionState;
    onSelectGroup: (mode: GroupMode) => void;
    onExpandAll: () => void;
    onCollapseAll: () => void;
}> = ({ searchValue, onSearchChange, groupMode, actionState, onSelectGroup, onExpandAll, onCollapseAll }) => (
    <>
        <Box sx={{ padding: '12px 10px 8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '30px', background: '#fff', boxShadow: '0px 12px 60px rgba(67, 71, 77, 0.08)' }}>
                <Box component="input" value={searchValue} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)} placeholder="Hasta arama..." sx={{ flex: 1, border: 'none', outline: 'none', fontFamily: poppins, fontSize: '14px', fontWeight: 500, letterSpacing: '-0.01em', color: '#111432', background: 'transparent', '&::placeholder': { color: '#B7B7B7', fontFamily: poppins } }} />
                <Box sx={{ color: '#2196F3', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </Box>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px 10px' }}>
            <BtnGroup label="Hasta" isActive={groupMode === 'hasta'} actionState={groupMode === 'hasta' ? actionState : null} onSelect={() => onSelectGroup('hasta')} onOpen={onExpandAll} onClose={onCollapseAll} />
            <BtnGroup label="Kategori" isActive={groupMode === 'kategori'} actionState={groupMode === 'kategori' ? actionState : null} onSelect={() => onSelectGroup('kategori')} onOpen={onExpandAll} onClose={onCollapseAll} />
        </Box>
    </>
);
